import { z } from "zod"

import { TenantIdSchema, UserIdSchema } from "./auth.js"
import { NotificationIdSchema } from "./id-schemas.js"

/**
 * Notification — recipient-targeted alert routed via one or more
 * channels.
 *
 * Per PRD §9 (notifications) and the Draft-2 hardening C-comments:
 * - Heist banners are `severity: hard_nudge` + `display: banner`, NOT a
 *   4th severity tier. Display surface is decoupled from severity.
 * - Channels (`in_app`, `whatsapp`, `email`, …) are a tenant-managed
 *   registry day-one so the Evolution API → WA Business API path is a
 *   transport swap, not a copy migration. v1 enum holds the launch set; registry overlay
 *   lands with Settings → Channels in Phase 1.
 * - Recipient resolution is explicit `userId` → entity owner → Admin
 *   fallback (no silent drops). Resolution happens engine-side; the
 *   entity here is post-resolution.
 * - `templateKey` references the Notification template registry; title
 *   + body are rendered server-side at dispatch (storing rendered text
 *   keeps the client display deterministic if templates change).
 */

const Bounded = (max = 256) => z.string().min(1).max(max)
const IsoDateTime = z.string().datetime()

export const NotificationChannelSchema = z.enum(["in_app", "whatsapp", "email"])
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>

export const NotificationSeveritySchema = z.enum(["info", "warning", "critical", "hard_nudge"])
export type NotificationSeverity = z.infer<typeof NotificationSeveritySchema>

export const NotificationDisplaySchema = z.enum(["default", "banner"])
export type NotificationDisplay = z.infer<typeof NotificationDisplaySchema>

export const NotificationEntityRefSchema = z.object({
	type: z.enum(["shipment", "document", "lot", "party", "merchant_change", "correction_round"]),
	id: Bounded(256),
})
export type NotificationEntityRef = z.infer<typeof NotificationEntityRefSchema>

export const NotificationSchema = z.object({
	id: NotificationIdSchema,
	tenantId: TenantIdSchema,
	userId: UserIdSchema,
	channel: NotificationChannelSchema,
	severity: NotificationSeveritySchema,
	display: NotificationDisplaySchema,
	templateKey: Bounded(128),
	title: Bounded(256),
	body: z.string().max(8192),
	ctaUrl: z.string().url().max(2048).optional(),
	entity: NotificationEntityRefSchema.optional(),
	dispatchedAt: IsoDateTime.optional(),
	deliveredAt: IsoDateTime.optional(),
	readAt: IsoDateTime.optional(),
	dismissedAt: IsoDateTime.optional(),
	createdAt: IsoDateTime,
	updatedAt: IsoDateTime,
})
export type Notification = z.infer<typeof NotificationSchema>
